import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from "@mui/material";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VerifiedIcon from '@mui/icons-material/Verified';

const CompactAccountCard = ({ icon, label, value, color = "#2f6bff", sensitive = false }) => {
  const [showValue, setShowValue] = useState(!sensitive);

  const displayValue = () => {
    if (!value) return "Not Provided";
    if (sensitive && !showValue) {
      return `••••${value.slice(-4)}`;
    }
    return value;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: '16px',
        border: '1px solid #eef0f4',
        height: '100%',
        transition: 'all 0.2s ease',
        position: 'relative',
        '&:hover': {
          borderColor: `${color}40`,
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 18px ${color}14`
        }
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{
          width: 38,
          height: 38,
          borderRadius: '12px',
          backgroundColor: `${color}14`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 38,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 17 } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              display: 'block',
              fontSize: '0.65rem',
              lineHeight: 1.1,
              mb: 0.25,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '0.825rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: (sensitive && showValue) ? 'monospace' : 'inherit'
            }}
          >
            {displayValue()}
          </Typography>
        </Box>
        {sensitive && value && (
          <Tooltip title={showValue ? "Hide" : "Show"}>
            <IconButton
              size="small"
              onClick={() => setShowValue(!showValue)}
              sx={{
                width: 24,
                height: 24,
                color: color,
                '&:hover': {
                  backgroundColor: `${color}15`
                }
              }}
            >
              {showValue ? (
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

const EmpAccount = ({ data }) => {
  const accountInfo = [
    {
      icon: <AccountBalanceIcon />,
      label: "Bank Account",
      value: data?.bank_account_no,
      color: "#2f6bff",
      sensitive: true
    },
    {
      icon: <FingerprintIcon />,
      label: "Aadhar Number",
      value: data?.aadhaar_number,
      color: "#2f6bff",
      sensitive: true
    },
    {
      icon: <AccountBalanceWalletIcon />,
      label: "IFSC Code",
      value: data?.ifsc_code,
      color: "#2f6bff"
    },
    {
      icon: <CreditCardIcon />,
      label: "PAN Number",
      value: data?.pan_no,
      color: "#2f6bff",
      sensitive: true
    },
    {
      icon: <BusinessIcon />,
      label: "Cost Center",
      value: data?.level_cost_center,
      color: "#2f6bff"
    },
    {
      icon: <PhoneIcon />,
      label: "Contact",
      value: data?.mobile_number,
      color: "#2f6bff"
    },
    {
      icon: <EmailIcon />,
      label: "Email",
      value: data?.email,
      color: "#2f6bff"
    }
  ];

  return (
    <Box>
      <Grid container spacing={1.5}>
        {accountInfo.map((info, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <CompactAccountCard {...info} />
          </Grid>
        ))}
      </Grid>

      {/* Verification Badge */}
      <Box sx={{ mt: 2 }}>
        <Chip
          icon={<VerifiedIcon sx={{ fontSize: 15 }} />}
          label="Account Verified"
          color="success"
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 26
          }}
        />
      </Box>
    </Box>
  );
};

export default EmpAccount;